import { useQuery } from '@tanstack/react-query'
import { dashboardApi, type DashboardQueryWindow } from '@/api/dashboardApi'
import type { ProcedureType } from '@/types/cgt'
import { useTenant } from '@/providers/TenantProvider'

/**
 * The dashboard widgets' queries (z8nrz7eqyj).
 *
 * The tenant is in every key: these are aggregates over one tenant's data, and
 * a cached number from the tenant just left must never paint. Widgets that
 * read the same endpoint over the same window share one request through the
 * key, so three procedure widgets on a dashboard cost one call.
 *
 * A dashboard is looked at, not worked in, so its numbers refresh every
 * minute while it is open rather than staying at the app's 5-minute staleness.
 */
const REFRESH_MS = 60_000

function useTenantQuery<T>(key: unknown[], fn: () => Promise<T>) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery<T>({
    queryKey: ['dashboard', tenantId, ...key],
    queryFn: fn,
    enabled: !!tenantId,
    staleTime: REFRESH_MS,
    refetchInterval: REFRESH_MS,
  })
}

export function useProceduresSummary(window: DashboardQueryWindow) {
  return useTenantQuery(['procedures', 'summary', window.from, window.to], () =>
    dashboardApi.proceduresSummary(window),
  )
}

export function useProcedurePipeline() {
  return useTenantQuery(['procedures', 'pipeline', 'Transplant'], () => dashboardApi.pipeline('Transplant'))
}

export function useAtRiskProcedures() {
  return useTenantQuery(['procedures', 'at-risk'], () => dashboardApi.atRisk(10))
}

export function useUpcomingProcedures() {
  return useTenantQuery(['procedures', 'upcoming', 7], () => dashboardApi.upcoming(7, 10))
}

export function usePatientsSummary(window: DashboardQueryWindow) {
  return useTenantQuery(['patients', 'summary', window.from, window.to], () =>
    dashboardApi.patientsSummary(window),
  )
}

export function useFormsSummary(window: DashboardQueryWindow) {
  return useTenantQuery(['forms', 'summary', window.from, window.to], () => dashboardApi.formsSummary(window))
}

export function useProcessSteps(window: DashboardQueryWindow) {
  return useTenantQuery(['process', 'steps', window.from, window.to], () => dashboardApi.processSteps(window))
}

export function useProcedureTypeSummary(type: ProcedureType, window: DashboardQueryWindow) {
  return useTenantQuery(['procedures', 'type-summary', type, window.from, window.to], () =>
    dashboardApi.procedureTypeSummary(type, window),
  )
}

export function useKitBuilds(window: DashboardQueryWindow) {
  return useTenantQuery(['kits', 'builds', window.from, window.to], () => dashboardApi.kitBuilds(window))
}

export function usePickLists() {
  return useTenantQuery(['pick-lists'], () => dashboardApi.pickLists())
}

const windowed = <T,>(name: string, fn: (w: DashboardQueryWindow) => Promise<T>) =>
  function useWindowed(window: DashboardQueryWindow) {
    return useTenantQuery([name, window.from, window.to], () => fn(window))
  }

export const useInventoryDashboard = windowed('inventory', dashboardApi.inventory)
export const useProductsDashboard = windowed('products', dashboardApi.products)
export const useEquipmentDashboard = windowed('equipment', dashboardApi.equipment)
export const useCapaDashboard = windowed('capa', dashboardApi.capa)
export const useNotificationsDashboard = windowed('notifications', dashboardApi.notifications)
export const useAuditDashboard = windowed('audit', dashboardApi.audit)
export const useIntegrationsDashboard = windowed('integrations', dashboardApi.integrations)
